import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import * as Yup from "yup";
import moment from "moment";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";
import DateField from "Components/Common/DatePicker";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import getFileExtension from "utils/getFileExtension";
import { getImageUrl, onlyNumberKeys } from "utils";

import { usePostAddRefundDetailsMutation } from "apis/UtilityConsumer/utilityConsumer";
import {
  useGetConsumerBankAndPaymentDetails,
  usePutReleaseConnectionMutation,
} from "apis/ApplicationRequestDetail";

import pdfImg from "assets/images/pdf.png";

const RefundBankDetailsFormModal = ({
  handleClose,
  applicationId,
  utilityConsumerId,
  isUtilityConsumerModal,
  t,
}) => {
  const [releaseConnection, { isLoading }] = usePutReleaseConnectionMutation();
  const [addRefundDetails, { isLoading: isRefundDetailsAdding }] =
    usePostAddRefundDetailsMutation();
  const {
    data: { data: { bankDetail, refundDetail } = {} } = {},
    isLoading: isBankDetailsLoading,
  } = useGetConsumerBankAndPaymentDetails(utilityConsumerId);

  const {
    beneficiaryName,
    bankAccountNumber,
    bankName,
    branchName,
    ifscCode,
    cancelChequeUrl,
  } = bankDetail || {};

  const initialValues = useMemo(
    () => ({
      utrNumber: refundDetail?.utrNumber ?? "",
      paymentDate: refundDetail?.paymentDate
        ? convertUTCDateToLocalDate(refundDetail.paymentDate)
        : "",
      amount: refundDetail?.amount ?? "",
    }),
    [refundDetail]
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      utrNumber: Yup.string().required("UTR number is required"),
      paymentDate: Yup.date()
        .required("Payment date is required")
        .test("isValidPayment", "Enter past date", (_, context) => {
          const [{ value: { paymentDate } = {} } = {}] = context.from || [];

          if (!paymentDate) {
            return context.createError({ message: "Enter a valid date" });
          }
          return paymentDate.isBefore(moment());
        }),
      amount: Yup.string().required("amount is required"),
    }),
    onSubmit: async (values) => {
      if (isUtilityConsumerModal) {
        await addRefundDetails(utilityConsumerId, {
          ...values,
          paymentDate: values.paymentDate.format("YYYY-MM-DD"),
        });
      } else {
        await releaseConnection({
          applicationId,
          payload: {
            ...values,
            paymentDate: values.paymentDate.format("YYYY-MM-DD"),
          },
          toastMessage: "Application Status update to Refunded",
        });
      }
      handleClose();
    },
  });

  const { handleSubmit } = formik;

  if (isBankDetailsLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      title="Refund Bank Information"
      handleClose={handleClose}
      maxWidth="md"
    >
      <Grid container columnSpacing={2} className="mt-2">
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("UTR Number") + "*"}
            name="utrNumber"
            placeholder="Enter UTR Number"
            type="text"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            formik={formik}
            label={t("Amount") + "*"}
            name="amount"
            placeholder="Enter Amount"
            InputProps={{
              inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e, true) },
            }}
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DateField
            label={t("Payment Date") + "*"}
            name="paymentDate"
            formik={formik}
            format="DD-MM-YYYY"
            maxDate={moment()}
            placeholder="Select Payment Date"
          />
        </Grid>

        {!!bankDetail && (
          <>
            <Grid item xs={12} className="mb-2">
              <Typography variant="h6">
                {t("Consumer Bank Details")} :
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Beneficiary name")}: </b>
                {beneficiaryName}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Account Number")}: </b>
                {bankAccountNumber}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Branch name")}: </b>
                {branchName}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("IFSC Code")}: </b>
                {ifscCode}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Bank name")}: </b>
                {bankName}
              </Text>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <b>{t("Cancelation Check")} :</b>
              </Typography>
            </Grid>
            <Grid xs={12}>
              <div className="image-container ms-3 mt-1">
                <a
                  href={getImageUrl(cancelChequeUrl)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Box
                    component="img"
                    className="image d-block m-auto"
                    alt="document image"
                    src={
                      getFileExtension(cancelChequeUrl.toLowerCase()) === "pdf"
                        ? pdfImg
                        : getImageUrl(cancelChequeUrl)
                    }
                  />
                </a>
              </div>
            </Grid>
          </>
        )}
      </Grid>
      <Box className="d-flex justify-content-end">
        <Button
          size="medium"
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || isRefundDetailsAdding}
        >
          {t("Submit")}
          {(isLoading || isRefundDetailsAdding) && (
            <CircularProgress size={20} />
          )}
        </Button>
      </Box>
    </Modal>
  );
};

RefundBankDetailsFormModal.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  utilityConsumerId: PropTypes.number,
  isUtilityConsumerModal: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(RefundBankDetailsFormModal);
